import moment from 'moment';
import { takeEvery, put, select, call } from 'redux-saga/effects';
import 'whatwg-fetch';
import * as actionTypes from 'constants/actionTypes';
import { trackEvent } from 'actions/events';
import { selectBillingDetails } from 'reducers/billing';
import { selectAccountMeta } from 'reducers/account/meta';
import { selectUser } from 'reducers/user';
import { getSlackChurnString } from 'utils/billing';
import { reportError } from 'helpers/error-reporting';
import { updateAccountMeta } from 'actions/account/meta';
import { REPLACE_ACCOUNT_META } from 'constants/account/meta';

// This saga is in here in case we ever allow self-service
// cancellation. It goes with the `add-invoices-endpoint`
// branch in the billing api

// function* cancelSubscription(action) {
//   const { stripeId, subscriptionId } = action.payload;

//   const isStripeCustomer = stripeId && stripeId.startsWith('cus_');

//   if (!isStripeCustomer || !subscriptionId) {
//     yield put({
//       type: actionTypes.CANCEL_SUBSCRIPTION_FAILED,
//     });
//     return;
//   }

//   try {
//     const fetchBillingDetails = (stripeId, subscriptionId) => {
//       return fetch(
//         `${BILLING_API_URL}/subscriptions/${stripeId}/${subscriptionId}`,
//         {
//           method: 'DELETE',
//         }
//       );
//     };

//     yield put({ type: actionTypes.CANCEL_SUBSCRIPTION_FETCHING });

//     const apiResponse = yield call(
//       fetchBillingDetails,
//       stripeId,
//       subscriptionId
//     );
//     yield put({
//       type: actionTypes.CANCEL_SUBSCRIPTION_SUCCESS,
//     });

//     if (apiResponse.status === 200) {
//       yield put({
//         type: actionTypes.CANCEL_SUBSCRIPTION_SUCCESS,
//       });
//     } else {
//       yield put({
//         type: actionTypes.CANCEL_SUBSCRIPTION_FAILED,
//       });
//       throw apiResponse.json();
//     }
//   } catch (err) {
//     yield put({
//       type: actionTypes.CANCEL_SUBSCRIPTION_FAILED,
//     });
//     console.error(err);
//   }
// }

function* removeCancelOnResubscribe() {
  const accountMeta = yield select(selectAccountMeta);

  if (accountMeta.canceledAt) {
    yield put(updateAccountMeta({ canceledAt: null }));
  }
}

function* trackCancelSubscription(action) {
  const { fields, timestamp } = action.payload;

  const billingDetails = yield select(selectBillingDetails);
  const firstSubscription =
    billingDetails &&
    billingDetails.subscriptions &&
    billingDetails.subscriptions.data &&
    billingDetails.subscriptions.data[0];
  const plan = firstSubscription && firstSubscription.plan;
  const planName = plan && plan.name;

  const subscriptionStart = firstSubscription && firstSubscription.created;
  const formattedSubscriptionStart =
    subscriptionStart && moment(subscriptionStart * 1000).format('MM/DD/YY');

  const formattedCancelDate =
    plan &&
    moment(firstSubscription.current_period_end * 1000).format('MM/DD/YY');

  const billingEmail = billingDetails && billingDetails.email;

  const accountMeta = yield select(selectAccountMeta);
  const accountId = accountMeta && accountMeta.id;
  const companyName = accountMeta && accountMeta.company;

  const accountCreated = accountMeta && accountMeta.createdAt;
  const formattedAccountCreated =
    accountCreated && moment(accountCreated).format('MM/DD/YY');

  const user = yield select(selectUser);
  const userEmail = user && user.email;

  const formattedRequestTimestamp = moment(timestamp).format('MM/DD/YY');

  const cancellationInfo = {
    expectedChurnDate: formattedCancelDate,
    userEmail,
    accountId,
    requestDate: formattedRequestTimestamp,
    companyName,
    billingEmail,
    planName,
    subscriptionStart: formattedSubscriptionStart,
    accountCreated: formattedAccountCreated,
  };

  const segmentPayload = { ...cancellationInfo, ...fields };

  try {
    yield put(trackEvent('Subscription Canceled', { ...segmentPayload }));

    const slackMessage = getSlackChurnString(cancellationInfo, fields);
    /* global SUB_SLACK_HOOK_URL */
    const webhookResponse = yield fetch(SUB_SLACK_HOOK_URL, {
      method: 'POST',
      body: JSON.stringify({ text: slackMessage }),
    });

    if (!webhookResponse.ok) {
      reportError('Slack hook failed', segmentPayload);
    }

    yield put(updateAccountMeta({ canceledAt: timestamp }));
  } catch {
    reportError('Subscription Automation Failed', segmentPayload);
  }
}

function* fetchUrlRollup(action) {
  const { accountId, accountUuid } = action.payload;

  try {
    const requestUrlRollup = (id, uuid) => {
      /* global APPCUES_ESTIMATION_API_URL */
      return fetch(`${APPCUES_ESTIMATION_API_URL}/accounts/${id}/urls/rollup`, {
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
        },
        body: JSON.stringify({
          account_id: id,
          account_api_key: uuid,
        }),
      });
    };

    const apiResponse = yield call(requestUrlRollup, accountId, accountUuid);

    if (apiResponse.status === 200) {
      const rollup = yield call([apiResponse, apiResponse.json]);

      yield put({
        type: actionTypes.REPLACE_URL_ROLLUP,
        payload: rollup.topUrls,
      });
    } else {
      throw apiResponse.json();
    }
  } catch (error) {
    reportError(error, { accountId, accountUuid });
  }
}

function* fetchBillingDetails(action) {
  const { stripeId } = action.payload;

  const isStripeCustomer = stripeId && stripeId.startsWith('cus_');

  if (!isStripeCustomer) return;

  try {
    const requestBillingDetails = id => {
      /* global BILLING_API_URL */
      return fetch(`${BILLING_API_URL}/customers/${id}`);
    };

    const apiResponse = yield call(requestBillingDetails, stripeId);

    if (apiResponse.status === 200) {
      const billingDetails = yield call([apiResponse, apiResponse.json]);

      yield put({
        type: actionTypes.REPLACE_BILLING_DETAILS,
        payload: billingDetails.data,
      });
    } else {
      throw apiResponse.json();
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
}

function* fetchBillingHistory(action) {
  const { stripeId } = action.payload;

  const isStripeCustomer = stripeId && stripeId.startsWith('cus_');

  if (!isStripeCustomer) return;

  try {
    const requestBillingHistory = id => {
      return fetch(`${BILLING_API_URL}/customers/${id}/invoices`);
    };

    const apiResponse = yield call(requestBillingHistory, stripeId);

    if (apiResponse.status === 200) {
      const billingHistory = yield call([apiResponse, apiResponse.json]);

      yield put({
        type: actionTypes.REPLACE_BILLING_HISTORY,
        payload: billingHistory.data,
      });
    } else {
      throw apiResponse.json();
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
}

function* stripe() {
  yield takeEvery(actionTypes.PAYMENT_SUCCESS, removeCancelOnResubscribe);
  yield takeEvery(REPLACE_ACCOUNT_META, fetchBillingDetails);
  yield takeEvery(REPLACE_ACCOUNT_META, fetchBillingHistory);
  yield takeEvery(actionTypes.FETCH_URL_ROLLUP, fetchUrlRollup);
  yield takeEvery(
    actionTypes.TRACK_CANCEL_SUBSCRIPTION,
    trackCancelSubscription
  );
  // yield takeEvery(actionTypes.CANCEL_SUBSCRIPTION, cancelSubscription);
}

export default stripe;
